import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { allKnowledgeCards } from '../data/knowledgeCards';
import { UserRole } from '../types';
import styles from './KnowledgeCards.module.css';

const KnowledgeCards: React.FC = () => {
  const navigate = useNavigate();
  const { levelId } = useParams<{ levelId: string }>();
  const userRole = useAppStore((s) => s.userRole);
  const navigatedRef = useRef(false);

  // 稳定化关卡卡片数组引用
  const allCards = useMemo(
    () => allKnowledgeCards[levelId || ''] || [],
    [levelId]
  );

  // 根据用户角色过滤知识卡片
  const cards = useMemo(() => {
    return allCards.filter((card) => {
      if (!card.roles || card.roles.length === 0) return true; // 无限制，所有人可见
      return card.roles.includes(userRole as UserRole);
    });
  }, [allCards, userRole]);

  // 如果没有知识卡片，直接跳到答题页（仅跳转一次）
  useEffect(() => {
    if (levelId && cards.length === 0 && !navigatedRef.current) {
      navigatedRef.current = true;
      navigate(`/quiz/${levelId}`, { replace: true });
    }
  }, [levelId, cards.length]);

  const [currentCard, setCurrentCard] = useState(0);

  if (cards.length === 0) {
    return (
      <div className={styles.container}>
        <p>正在跳转到答题...</p>
      </div>
    );
  }

  const card = cards[currentCard];
  const isLast = currentCard === cards.length - 1;
  const isFirst = currentCard === 0;

  return (
    <div className={styles.container}>
      {/* 顶部导航 */}
      <div className={styles.topBar}>
        <button className={styles.homeLink} onClick={() => navigate(-1)}>
          ← 返回
        </button>
      </div>
      {/* 进度指示器 */}
      <div className={styles.progressBar}>
        {cards.map((_, idx) => (
          <div
            key={idx}
            className={`${styles.dot} ${idx <= currentCard ? styles.dotActive : ''}`}
          />
        ))}
      </div>
      <div className={styles.progressText}>
        知识卡片 {currentCard + 1} / {cards.length}
      </div>

      {/* 卡片 */}
      <div className={styles.cardWrapper}>
        <div className={styles.card}>
          <div className={styles.cardNumber}>
            <span>第 {card.order} 张卡片</span>
          </div>
          <h2 className={styles.cardTitle}>{card.title}</h2>

          <div className={styles.oneLineBox}>
            <div className={styles.oneLineIcon}>💡</div>
            <p className={styles.oneLineText}>{card.oneLineSummary}</p>
          </div>

          <div className={styles.detailBox}>
            <h3 className={styles.detailTitle}>详细说明</h3>
            <p className={styles.detailText}>{card.detailedExplanation}</p>
          </div>

          <div className={styles.misconceptionBox}>
            <h3 className={styles.misconceptionTitle}>⚠️ 常见误区</h3>
            <p className={styles.misconceptionText}>{card.commonMisconception}</p>
          </div>

          {card.order === 3 && (
            <div className={styles.boundaryBox}>
              <p>🩺 本内容仅用于育儿知识学习，不作为医疗诊断依据。</p>
            </div>
          )}
        </div>

        {/* 导航 */}
        <div className={styles.nav}>
          {!isFirst && (
            <button
              className={styles.navBtn}
              onClick={() => setCurrentCard(currentCard - 1)}
            >
              ← 上一张
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            className={styles.navBtnPrimary}
            onClick={() => {
              if (isLast) {
                navigate(`/quiz/${levelId}`);
              } else {
                setCurrentCard(currentCard + 1);
              }
            }}
          >
            {isLast ? '开始答题 →' : '下一张 →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeCards;